// Transaction signing for pearl-1, done by hand.
//
// WHY THIS FILE EXISTS. @gnolang/gno-js-client 3.x renders the sign doc's fee
// as {"amount":[{amount,denom}],"gas"} (gnolang/gno#6173, the Ledger shape).
// pearl-1 runs a chain build from 2026-08-27 that predates #6173 and still
// verifies signatures over {"gas_fee":"<coin>","gas_wanted":"<n>"}, so every
// transaction the stock GnoWallet signs is rejected with "signature
// verification failed". The library also hard-codes session_addr: "" in the
// signature, which rules out session-key signing entirely.
//
// Both are fixed here: the fee is rendered the way the chain expects, and the
// signature may carry a session address. Proven live on pearl-1 by
// scripts/prove-session.mjs. When pearl-1 upgrades past #6173, flip FEE_SHAPE.
import { Tx, PubKeySecp256k1, Secp256k1PubKeyType, sortedJsonStringify, encodeCharacterSet, stringToUTF8, extractSimulateFromResponse } from '@gnolang/tm2-js-client';
import { decodeTxMessages } from '@gnolang/gno-js-client';

// 'legacy' = pre-#6173 (pearl-1 today), 'ledger' = post-#6173.
export const FEE_SHAPE = 'legacy';

export function renderFee(fee, shape = FEE_SHAPE) {
  if (shape === 'ledger') {
    const m = /^(\d+)([a-z][a-z0-9/]*)$/i.exec(fee.gas_fee);
    if (!m) throw new Error(`invalid coin: ${fee.gas_fee}`);
    return { amount: [{ amount: m[1], denom: m[2] }], gas: fee.gas_wanted.toString(10) };
  }
  return { gas_fee: fee.gas_fee, gas_wanted: fee.gas_wanted.toString(10) };
}

// Amino JSON honours `omitempty`; ts-proto's toJSON does not. These are the
// omitempty fields on the messages this page signs (tm2/pkg/sdk/auth/msgs.go,
// gno.land/pkg/sdk/vm/msgs.go). Verified against the chain's own
// std.GetSignaturePayload for all three message types: test/sign.test.mjs
// pins the exact bytes the chain produced.
export function aminoOmitEmpty(msg) {
  const m = { ...msg };
  switch (m['@type']) {
    case '/auth.m_create_session':
      if (!m.allow_paths || m.allow_paths.length === 0) delete m.allow_paths;
      if (!m.spend_limit) delete m.spend_limit;
      if (m.spend_period === undefined || String(m.spend_period) === '0') delete m.spend_period;
      break;
    case '/vm.m_call':
      if (!m.args || m.args.length === 0) delete m.args;
      break;
    default:
  }
  return m;
}

// The bytes that get signed: sortJSON(aminoJSON(SignDoc)). Amino renders
// int64/uint64 as strings, which is why every number here is a string.
export function signBytes({ chainId, accountNumber, sequence, tx }) {
  const doc = {
    chain_id: chainId,
    account_number: String(accountNumber),
    sequence: String(sequence),
    fee: renderFee(tx.fee),
    msgs: decodeTxMessages(tx.messages).map(aminoOmitEmpty),
    memo: tx.memo ?? '',
  };
  return stringToUTF8(encodeCharacterSet(sortedJsonStringify(doc)));
}

// Signs tx with a signer (anything with getPublicKey/signData, e.g. a
// tm2-js-client KeySigner). accountNumber/sequence belong to the account that
// holds the key: the master's for a master key, the SESSION account's for a
// session key. sessionAddr, when given, marks the signature as a session
// signature; the message caller stays the master.
export async function signTx({ signer, chainId, accountNumber, sequence, tx, sessionAddr = '' }) {
  if (!tx.fee) throw new Error('tx has no fee');
  const bytes = signBytes({ chainId, accountNumber, sequence, tx });
  const pub = await signer.getPublicKey();
  const signature = {
    pub_key: { type_url: Secp256k1PubKeyType, value: PubKeySecp256k1.encode({ key: pub }).finish() },
    signature: await signer.signData(bytes),
    session_addr: sessionAddr,
  };
  return { ...tx, signatures: [...(tx.signatures ?? []), signature] };
}

export function encodeTxBase64(tx) {
  const raw = Tx.encode(tx).finish();
  if (typeof btoa === 'function') {
    let s = '';
    for (let i = 0; i < raw.length; i++) s += String.fromCharCode(raw[i]);
    return btoa(s);
  }
  return Buffer.from(raw).toString('base64');
}

export const COMMIT = 'broadcast_tx_commit'; // returns after the block, with deliver_tx
export const SYNC = 'broadcast_tx_sync';     // returns after the mempool check, hash only

// Sizes the fee by simulation: sign once with a huge budget, ask the node
// what it used, then budget that plus a margin. pearl-1's minimum gas price
// is 1ugnot/1000gas; gasPrice is in ugnot per 1000 gas.
// .app/simulate UNDER-reports a session-signed call badly: measured 3.3M
// simulated vs a true 11.9M in the block (deliver writes the session account
// and locks the storage deposit; simulate does neither). WORSE, below the
// real figure the call does not just fall short — it exhausts and reports
// gas_used ≈ gas_wanted, so an under-budget guess looks like a runaway. So the
// budget is margin×simulate floored at floorGas, both generous. The fee is a
// flat gas_fee, so a loose ceiling costs nothing a right-sized call didn't;
// gasPrice 2 is twice pearl-1's floor (1ugnot/1000gas). Master-signed calls
// simulate accurately (~2.0M), so the same margin is harmless for them.
export async function estimateFee({ provider, signer, chainId, accountNumber, sequence, tx, sessionAddr = '', gasPrice = 2n, margin = 4.5, floorGas = 12_000_000n, maxGas = 3_000_000_000n }) {
  const probe = { ...tx, fee: { gas_wanted: maxGas, gas_fee: `${(maxGas * gasPrice) / 1000n}ugnot` } };
  const signed = await signTx({ signer, chainId, accountNumber, sequence, tx: probe, sessionAddr });
  // Not provider.estimateGas(): tm2-js-client 3.3.0 sends the base64 TEXT of
  // the tx as the query data, and the node's app.Simulate(req.Data) wants the
  // raw tx bytes — pearl-1 answers "unable to decode tx". Raw bytes work.
  const resp = await provider.abciQuery({ path: '.app/simulate', data: Tx.encode(signed).finish(), height: 0, prove: false });
  const used = BigInt(extractSimulateFromResponse(resp).gas_used);
  let gas_wanted = (used * BigInt(Math.round(margin * 100))) / 100n;
  if (gas_wanted < floorGas) gas_wanted = floorGas;
  return { gas_wanted, gas_fee: `${(gas_wanted * gasPrice) / 1000n + 1n}ugnot`, used };
}

// Signs and broadcasts. Throws a typed tm2 error on mempool rejection; a
// failure inside the realm call comes back in result.deliver_tx.ResponseBase.
// With estimate: true the fee is sized by simulation first (tx.fee ignored).
export async function signAndSend({ provider, signer, accountNumber, sequence, tx, sessionAddr = '', endpoint = COMMIT, estimate = false, gasPrice }) {
  const chainId = (await provider.getStatus()).node_info.network;
  let fee = tx.fee;
  if (estimate) fee = await estimateFee({ provider, signer, chainId, accountNumber, sequence, tx, sessionAddr, gasPrice });
  const signed = await signTx({ signer, chainId, accountNumber, sequence, tx: { ...tx, fee }, sessionAddr });
  const r = await provider.sendTransaction(encodeTxBase64(signed), endpoint);
  return Object.assign(r ?? {}, { fee });
}
